import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, CheckCircle2, PlayCircle } from "lucide-react";
import TopRatedMakers from "../components/TopRatedMakers";
import FiveUserTypes from "../components/FiveUserTypes";
import LaunchVideoSection from "../components/LaunchVideoSection";

const HERO = "https://images.pexels.com/photos/6646917/pexels-photo-6646917.jpeg?auto=compress&cs=tinysrgb&w=1600";
const MARKET1 = "https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=1600";
const MARKET2 = "https://images.pexels.com/photos/3184338/pexels-photo-3184338.jpeg?auto=compress&cs=tinysrgb&w=1600";
const POTTERY = "https://images.pexels.com/photos/7679658/pexels-photo-7679658.jpeg?auto=compress&cs=tinysrgb&w=1200";
const MAKER_A = "https://images.pexels.com/photos/2381069/pexels-photo-2381069.jpeg?auto=compress&cs=tinysrgb&w=800";
const MAKER_B = "https://images.pexels.com/photos/1181391/pexels-photo-1181391.jpeg?auto=compress&cs=tinysrgb&w=800";
const MAKER_C = "https://images.pexels.com/photos/1181605/pexels-photo-1181605.jpeg?auto=compress&cs=tinysrgb&w=800";

export default function Landing() {
  return (
    <div>
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-14 pb-24">
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <div className="overline" style={{ color: "var(--avt-pink)" }}>Add your event · Book vendors · Discover more</div>
            <h1 className="font-serif mt-6 text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
              <span style={{ color: "var(--avt-teal)" }}>Add</span>{" "}
              <span style={{ color: "var(--avt-purple)" }}>your event.</span>
              <br />
              <span style={{ color: "var(--avt-pink)" }}>Book vendors.</span>{" "}
              <span style={{ color: "var(--avt-orange)" }}>Discover more.</span>
            </h1>
            <p className="mt-8 max-w-lg text-lg leading-relaxed" style={{ color: "var(--ink-soft)" }}>
              Discover craft fairs, food markets and pop-ups near you. Apply with your portfolio in minutes.
              AddVendTour connects independent vendors with local event organizers who value their story.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/events" data-testid="hero-browse-events" className="btn-primary px-7 py-3.5 rounded-full inline-flex items-center gap-2">
                Browse events <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/register" data-testid="hero-register" className="btn-outline px-7 py-3.5 rounded-full">
                Post an event
              </Link>
            </div>
          </div>
          <div className="md:col-span-5 relative">
            <img src={HERO} alt="Diverse volunteers sharing food and water at a community event" className="w-full h-[520px] object-cover rounded-sm" />
            <div className="absolute -bottom-6 -left-6 hidden md:block card-warm p-5 max-w-[220px]">
              <div className="overline" style={{ color: "var(--moss)" }}>This weekend</div>
              <div className="font-serif text-xl mt-1">42 events open</div>
              <div className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>across 18 cities</div>
            </div>
          </div>
        </div>
      </section>

      {/* LAUNCH VIDEO — placeholder for the demo, URL wired later */}
      <LaunchVideoSection />

      {/* FIVE USER TYPES — Give Back Marketplace showcase */}
      <FiveUserTypes />

      {/* TOP-RATED (renders only when we have ≥ 3 quality vendors) */}
      <TopRatedMakers />

      {/* BENTO */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-24">
        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-5 card-warm p-8 rounded-sm">
            <div className="overline" style={{ color: "var(--terracotta)" }}>For vendors</div>
            <h3 className="font-serif text-3xl mt-3">Apply with your portfolio</h3>
            <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>
              Build a warm, visual profile. Filter events by city, date and category. Track every application from pending to approved.
            </p>
            <ul className="mt-6 space-y-2 text-sm">
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" style={{ color: "var(--moss)" }} /> Portfolio with photos & bio</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" style={{ color: "var(--moss)" }} /> Filter events by location & category</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" style={{ color: "var(--moss)" }} /> Pay booth fees securely with Stripe</li>
            </ul>
          </div>
          <div className="md:col-span-7 relative overflow-hidden rounded-sm min-h-[280px]">
            <img src={MARKET1} alt="Local market" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(44,42,41,0.15), rgba(44,42,41,0.55))" }} />
            <div className="relative p-8 h-full flex flex-col justify-end text-white">
              <div className="overline">Discovery</div>
              <div className="font-serif text-4xl mt-2 max-w-md">Curated, local, always in season.</div>
            </div>
          </div>
          <div className="md:col-span-4 card-warm p-8 rounded-sm">
            <MapPin className="h-6 w-6" style={{ color: "var(--terracotta)" }} />
            <div className="font-serif text-2xl mt-3">Near you</div>
            <p className="text-sm mt-2" style={{ color: "var(--ink-soft)" }}>Filter by city and neighbourhood — no more scrolling through irrelevant events.</p>
          </div>
          <div className="md:col-span-4 card-warm p-8 rounded-sm">
            <Calendar className="h-6 w-6" style={{ color: "var(--terracotta)" }} />
            <div className="font-serif text-2xl mt-3">Fits your calendar</div>
            <p className="text-sm mt-2" style={{ color: "var(--ink-soft)" }}>Only see events on the dates you're available. One less spreadsheet.</p>
          </div>
          <div className="md:col-span-4 relative overflow-hidden rounded-sm min-h-[220px]">
            <img src={POTTERY} alt="Pottery" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* FEATURED MAKERS */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <div className="overline" style={{ color: "var(--moss)" }}>Featured makers</div>
            <h2 className="font-serif text-4xl sm:text-5xl mt-3 max-w-2xl">Real people. Real hands. Real stories.</h2>
          </div>
          <Link to="/events" className="btn-outline px-5 py-2.5 rounded-full text-sm" data-testid="featured-view-events">See where they're selling →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { img: MAKER_A, name: "Amara Okonkwo", craft: "Ceramics & Stoneware", city: "Brooklyn, NY", quote: "AddVendTour got me into three markets in a month — I stopped chasing spreadsheets and started making again." },
            { img: MAKER_B, name: "Jordan Bakari",  craft: "Small-batch skincare",   city: "Atlanta, GA",  quote: "Organizers actually see my portfolio. Approvals come with a personal note, not a form email." },
            { img: MAKER_C, name: "Naomi Adekunle", craft: "Textiles & block prints", city: "Oakland, CA", quote: "Every booth I've booked here has been my kind of crowd. It's warm. It feels like home." },
          ].map((m) => (
            <article key={m.name} className="card-warm rounded-sm overflow-hidden" data-testid={`featured-maker-${m.name.split(" ")[0].toLowerCase()}`}>
              <div className="aspect-[4/5] overflow-hidden">
                <img src={m.img} alt={m.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <div className="overline" style={{ color: "var(--terracotta)" }}>{m.craft}</div>
                <div className="font-serif text-2xl mt-2">{m.name}</div>
                <div className="text-xs" style={{ color: "var(--ink-soft)" }}>{m.city}</div>
                <p className="mt-4 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>“{m.quote}”</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* SPLIT */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-24 grid md:grid-cols-2 gap-8">
        <div className="card-warm p-10 rounded-sm">
          <div className="overline" style={{ color: "var(--moss)" }}>Organizers</div>
          <h3 className="font-serif text-4xl mt-4">Fill your event with the right vendors.</h3>
          <p className="mt-4 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>
            Post your market, review applications with portfolios attached, approve who you want, and collect booth fees on the same page.
          </p>
          <Link to="/register" data-testid="split-organizer-cta" className="mt-8 inline-flex items-center gap-2 btn-primary px-6 py-3 rounded-full">
            Start hosting <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="relative overflow-hidden rounded-sm min-h-[380px]">
          <img src={MARKET2} alt="Market plants" className="absolute inset-0 w-full h-full object-cover" />
        </div>
      </section>
    </div>
  );
}
